import { computed, observable } from 'mobx';
import _ from 'lodash';
import type { ShareholderInfo } from '@this/domain/other_service_shareholder_info';
import type { IndividualTargetSuppliedItem } from '@this/domain/individual_target_supplied_item';
import type { MessageTemplate, OrderItemStatusLogInfo } from '@this/components/arrangement/virtual_counter/types';
import type OrderItem from '../order_item2';
import Trip, { convertTripResponseToArgs } from '../trip/trip2';
import type { TripResponseArgs } from '../trip/trip2';
import type Order from '../order2';
import BulkTicket from '../bulk_ticket2';
import type { BulkTicketArgs } from '../bulk_ticket2';
import ShareholderTicketList from '../shareholder_ticket_list/index';
import type { ShareholderTicketArgs } from '../shareholder_ticket/shareholder_ticket';
import type { TaxTypeArgs } from '../tax_type';
import TaxType from '../tax_type';
import type { SuppliedItemArgs } from '../supplied_item/supplied_item2';
import SuppliedItem from '../supplied_item/supplied_item2';
import PaymentMethodList from '../payment_method/payment_method_list';
import type { PaymentMethodArgs } from '../payment_method/payment_method';
import type { OrderItemStepTodoArgs, OrderItemStepTodoResponseArgs } from './order_item_step_todo';
import OrderItemStepTodo, { convertOrderItemStepTodoResponseToArgs } from './order_item_step_todo';
import type { TicketOprResponseArgs } from '../ticket_opr_response';
import type { OrderItemStatus } from '../order_item/order_item_status';

export interface OrderItemStepTodoFieldStoreResponseArgs {
  service_id: number;
  trip: TripResponseArgs;
  todo: OrderItemStepTodoResponseArgs;
  bulk_tickets: BulkTicketArgs[];
  shareholder_tickets: ShareholderTicketArgs[];
  tax_types: TaxTypeArgs[];
  supplied_items: SuppliedItemArgs[];
  payment_methods: PaymentMethodArgs[];
  shareholder_infos: ShareholderInfo[];
  individual_target_supplied_items: IndividualTargetSuppliedItem[];
  organization_plan: string;
  ticket_opr_response_price: TicketOprResponseArgs[] | null;
  next_step_status: keyof typeof OrderItemStatus | null;
  message_templates: MessageTemplate[];
  order_item_status_log_info: OrderItemStatusLogInfo | null;
}

export interface OrderItemStepTodoFieldStoreArgs {
  serviceId: number;
  trip: Trip;
  todo: OrderItemStepTodoArgs;
  bulkTickets: BulkTicketArgs[];
  shareholderTickets: ShareholderTicketArgs[];
  taxTypes: TaxTypeArgs[];
  suppliedItems: SuppliedItemArgs[];
  paymentMethods: PaymentMethodArgs[];
  shareholderInfos: ShareholderInfo[];
  individualTargetSuppliedItems: IndividualTargetSuppliedItem[];
  organizationPlan: string;
  ticketOprResponsePrice: TicketOprResponseArgs[] | null;
  nextStepStatus: keyof typeof OrderItemStatus | undefined;
  messageTemplates: MessageTemplate[];
  orderItemStatusLogInfo: OrderItemStatusLogInfo | null;
}

export const convertOrderItemStepTodoFieldStoreResponseArgs = (
  response: OrderItemStepTodoFieldStoreResponseArgs
) => {
  const showFee = true;

  const args: OrderItemStepTodoFieldStoreArgs = {
    serviceId: response.service_id,
    trip: new Trip(convertTripResponseToArgs(response.trip, showFee)),
    todo: convertOrderItemStepTodoResponseToArgs(response.todo),
    bulkTickets: response.bulk_tickets,
    shareholderTickets: response.shareholder_tickets,
    taxTypes: response.tax_types,
    suppliedItems: response.supplied_items,
    paymentMethods: response.payment_methods,
    shareholderInfos: response.shareholder_infos,
    individualTargetSuppliedItems: response.individual_target_supplied_items,
    organizationPlan: response.organization_plan,
    ticketOprResponsePrice: response.ticket_opr_response_price,
    nextStepStatus: response.next_step_status ?? undefined,
    messageTemplates: response.message_templates,
    orderItemStatusLogInfo: response.order_item_status_log_info
  };
  return args;
};

export class OrderItemStepTodoFieldStore {
  @observable serviceId: number;
  @observable traceId: number;
  @observable trip: Trip;
  @observable todo: OrderItemStepTodo;
  @observable bulkTickets: BulkTicket[];
  @observable shareholderTickets: ShareholderTicketList;
  @observable taxTypes: TaxType[];
  @observable suppliedItems: SuppliedItem[];
  @observable paymentMethods: PaymentMethodList;
  @observable shareholderInfos: ShareholderInfo[];
  @observable individualTargetSuppliedItems: IndividualTargetSuppliedItem[];
  @observable organizationPlan: string;
  @observable ticketOprResponsePrice: TicketOprResponseArgs[] | null;
  @observable nextStepStatus: keyof typeof OrderItemStatus | undefined;
  @observable messageTemplates: MessageTemplate[];
  @observable orderItemStatusLogInfo: OrderItemStatusLogInfo | null;

  constructor(args: OrderItemStepTodoFieldStoreArgs, traceId: number) {
    this.serviceId = args.serviceId;
    this.traceId = traceId;
    this.trip = args.trip;
    this.todo = new OrderItemStepTodo(args.todo);
    this.bulkTickets = args.bulkTickets.map(bulkTicketArgs => new BulkTicket(bulkTicketArgs));
    this.shareholderTickets = new ShareholderTicketList(args.shareholderTickets);
    this.taxTypes = args.taxTypes.map(taxTypeArgs => new TaxType(taxTypeArgs));
    this.suppliedItems = args.suppliedItems.map(suppliedItemArgs => new SuppliedItem(suppliedItemArgs));
    this.paymentMethods = new PaymentMethodList(args.paymentMethods);
    this.shareholderInfos = args.shareholderInfos;
    this.individualTargetSuppliedItems = args.individualTargetSuppliedItems;
    this.organizationPlan = args.organizationPlan;
    this.ticketOprResponsePrice = args.ticketOprResponsePrice;
    this.nextStepStatus = args.nextStepStatus;
    this.messageTemplates = args.messageTemplates;
    this.orderItemStatusLogInfo = args.orderItemStatusLogInfo;
    this.classNameForModifiedField = this.classNameForModifiedField.bind(this);
    if (this.orderItem) {
      this.orderItem.isEditing = true;
      const statusToSet = this.nextStepStatus ?? this.todo.step?.finishedStatus;
      if (statusToSet !== undefined) {
        this.orderItem.status = statusToSet;
      }
    }
  }

  @computed get props() {
    return {
      index: 0,
      orderItem: this.orderItem,
      bulkTickets: this.bulkTickets,
      shareholderTickets: this.shareholderTickets,
      taxTypes: this.taxTypes,
      suppliedItems: this.suppliedItems,
      paymentMethods: this.paymentMethods,
      shareholderInfos: this.shareholderInfos,
      individualTargetSuppliedItems: this.individualTargetSuppliedItems
    };
  }

  @computed get order(): Order | undefined {
    return this.trip.order;
  }

  @computed get orderItem(): OrderItem | undefined {
    return this.trip.order?.orderItems.find(orderItem => orderItem.traceId === this.traceId);
  }

  @computed get initialOrderItem(): OrderItem | undefined {
    if (!this.orderItem) return undefined;
    return this.trip.initialOrderItemFor(this.orderItem);
  }

  @computed get orderItemDiff(): Record<string, unknown> | null {
    if (!this.orderItem || !this.initialOrderItem) return null;
    return this.orderItem.diffWith(this.initialOrderItem);
  }

  @computed get updateParams() {
    return {
      order_item_step_id: this.todo.id,
      action_type: 'complete',
      trip_id: this.trip?.id,
      ...this.order?.updateParams
    };
  }

  classNameForModifiedField = (...path: (string | number)[]): string => {
    // 右ナビでorder_itemを追加した場合はinitialOrderItemが存在せず、orderItemDiffもない
    // したがってその場合は全項目変更されたものとする
    const modified = this.orderItemDiff ? _.has(this.orderItemDiff, path) : true;
    return modified ? 'virtual-counte-price-change-form__modified_field' : '';
  };
}
